<template>
  <CognitoBrandedShell>
    <div
      v-if="errorDetail"
      class="w-full max-w-md space-y-6 rounded-2xl border border-gray-200 bg-white p-8 text-left shadow-lg sm:text-center"
    >
      <div class="space-y-3">
        <p
          v-if="errorHeading"
          class="text-lg font-semibold leading-snug text-red-600"
        >
          {{ errorHeading }}
        </p>
        <p class="whitespace-pre-line text-base leading-relaxed text-gray-600">
          {{ errorDetail }}
        </p>
      </div>
      <p class="text-sm text-gray-500">
        Returning to the home screen in {{ redirectSecondsRemaining }} seconds…
      </p>
      <button
        type="button"
        class="w-full rounded-lg bg-[#9489F5] px-4 py-3 text-sm font-medium text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#9489F5] focus:ring-offset-2 focus:ring-offset-white"
        @click="returnToHome"
      >
        Return to home
      </button>
    </div>
    <BarLoader
      v-else
      variant="spinner"
      theme="light"
      contained
      message="Verifying your account…"
    />
  </CognitoBrandedShell>
</template>

<script lang="ts">
/**
 * CognitoCallbackView
 *
 * Handles the OAuth2 authorization code exchange after Cognito Hosted UI login.
 *
 * Two paths:
 *  1. Normal OIDC flow: cognitoSigninCallbackFromRedirect() (signup vs authorize UserManager).
 *  2. Fallback (user navigated directly to Cognito URL, no OIDC state stored):
 *     exchange the code directly with POST to the token endpoint.
 *
 * On error: "Return to home" clears Pinia + both OIDC UserManagers + flow keys, then Hosted UI logout
 * via {@link cognitoSignInFresh} (avoids HomeView mounting with stale tokens / redirect loops).
 * Disabled-account (401) clears session via clearCompanyAppSessionAndSetDisabledAlert.
 */

import { defineComponent, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import {
  clearCognitoSessionLocalOnly,
  cognitoSignInFresh,
  cognitoSigninCallbackFromRedirect,
} from "@/cognito/auth";
import { companyPostAuthRedirect } from "@/utils/companyPostAuthRedirect";
import { isCarlySignupSource } from "@/utils/companyManagerInviteAccess";
import {
  PENDING_SUBSCRIPTION_INVITE_SLUG_KEY,
  PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY,
} from "@/constants/subscriptionInvite";
import {
  clearCompanyAppSessionAndSetDisabledAlert,
  axiosResponseIsAccountDisabled,
} from "@/cognito/companyAccountDisabled";
import BarLoader from "@/components/shared/BarLoader.vue";
import CognitoBrandedShell from "@/components/cognito/CognitoBrandedShell.vue";
import {
  companyInviteErrorDisplayForCode,
  friendlySignInFailureDisplay,
  getCompanyInviteErrorCode,
  inviteInvalidFriendlyError,
  isInviteInvalidError,
} from "@/cognito/cognitoCallbackErrors";
import {
  broadcastAppSwitchAfterCompanyCallbackSuccess,
  clearCompanyCrossAppSwitchSiblingBroadcastPending,
} from "@/utils/tabAppSwitchBroadcast";
import {
  applyCompanyRailsSignInResponseToStore,
  type CompanyRailsSignInResponse,
} from "@/cognito/applyCompanyRailsSignInResponseToStore";
import {
  clearExpertToCompanyHandoffCookies,
  syncSwitchableCompanyHandoffCookie,
} from "@/cognito/crossAppLogoutCookie";
import { decodeJwtPayload, displayNameFromClaims } from "@/cognito/jwtClaims";
import type { Router } from "vue-router";

function clearPendingInviteTokensIfInvalidInviteError(err: unknown): void {
  if (isInviteInvalidError(err)) {
    sessionStorage.removeItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY);
    sessionStorage.removeItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY);
  }
}

/**
 * Show the company interstitial (message + timer) before sending the user to the expert app
 * with `company_invite_error` so the expert session on :8081 is preserved.
 */
function goToExpertInviteErrorInterstitial(router: Router, err: unknown): void {
  const code = getCompanyInviteErrorCode(err) ?? "company_email_invite_invalid";
  void router.replace({
    name: "CompanyInviteExpertRedirect",
    query: { company_invite_error: code },
  });
}

export default defineComponent({
  name: "CognitoCallbackView",
  components: { BarLoader, CognitoBrandedShell },

  setup() {
    const router = useRouter();
    const errorHeading = ref("");
    const errorDetail = ref("");
    const redirectSecondsRemaining = ref(10);
    let redirectIntervalId: ReturnType<typeof setInterval> | null = null;
    let isNavigatingHome = false;

    function clearRedirectCountdown(): void {
      if (redirectIntervalId !== null) {
        clearInterval(redirectIntervalId);
        redirectIntervalId = null;
      }
    }

    function startRedirectCountdown(): void {
      clearRedirectCountdown();
      redirectSecondsRemaining.value = 10;
      redirectIntervalId = window.setInterval(() => {
        if (redirectSecondsRemaining.value <= 1) {
          clearRedirectCountdown();
          void returnToHome();
          return;
        }
        redirectSecondsRemaining.value -= 1;
      }, 1000);
    }

    /**
     * Do not `router.push('/')` first — HomeView would mount and can re-trigger Hosted UI while
     * signup/sign-in UserManagers still held stale tokens.
     */
    async function returnToHome(): Promise<void> {
      if (isNavigatingHome) return;
      isNavigatingHome = true;
      clearRedirectCountdown();
      await clearCognitoSessionLocalOnly();
      await cognitoSignInFresh();
    }

    onUnmounted(() => {
      clearRedirectCountdown();
    });

    /** Send access token + email (+ optional name) to Rails; then match Frontegg landing when Cognito flag is on. */
    async function exchangeWithRails(
      accessToken: string,
      email: string,
      name?: string
    ) {
      const userPayload: Record<string, string> = {
        token: accessToken,
        email,
      };
      if (name?.trim()) {
        userPayload.name = name.trim();
      }
      const pendingSubscriptionSlug = sessionStorage.getItem(
        PENDING_SUBSCRIPTION_INVITE_SLUG_KEY
      );
      if (pendingSubscriptionSlug) {
        userPayload.subscription_invite_slug = pendingSubscriptionSlug;
      }
      const pendingCompanyEmailToken = sessionStorage.getItem(
        PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY
      );
      if (pendingCompanyEmailToken) {
        userPayload.company_invitation_token = pendingCompanyEmailToken;
      }
      const hadPendingManagerInvite = Boolean(
        pendingSubscriptionSlug || pendingCompanyEmailToken
      );

      const response = await axios.post(
        process.env.VUE_APP_SIGN_IN_URL as string,
        {
          // email is required by the Middleware::CompanyAuth Rack layer
          // (same pattern as the Frontegg path in HomeView.vue)
          user: userPayload,
          user_type: "company_user",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (!response.data.token) {
        throw new Error("Server did not return a session token.");
      }

      const data = response.data as CompanyRailsSignInResponse;

      applyCompanyRailsSignInResponseToStore(data, email);
      clearExpertToCompanyHandoffCookies();
      syncSwitchableCompanyHandoffCookie(
        data.email ?? email,
        Boolean(data.user?.switchable)
      );

      // Manager invite-only: block bare Carly SSO. Pending invite on this exchange → continue
      // (Rails ApplyPendingInviteOnLogin updates signup_source for SE invites).
      if (isCarlySignupSource(data.signup_source) && !hadPendingManagerInvite) {
        const display = companyInviteErrorDisplayForCode(
          "invite_required_no_expert_account"
        );
        errorHeading.value = display.heading;
        errorDetail.value = display.detail;
        await clearCognitoSessionLocalOnly();
        startRedirectCountdown();
        return;
      }

      const useCognitoCompanyFlow =
        process.env.VUE_APP_USE_COGNITO_AUTH === "true";

      if (useCognitoCompanyFlow) {
        companyPostAuthRedirect(
          router,
          Boolean(data.has_company),
          Boolean(data.personal_info_required)
        );
      } else {
        await router.push({ name: "ExpertPrograms" });
      }
    }

    /**
     * Fallback: exchange the authorization code directly with the Cognito
     * token endpoint when no OIDC state is stored (e.g. user navigated
     * directly to the Cognito Hosted UI URL rather than through signinRedirect).
     * Returns the full token response including id_token for email extraction.
     *
     * PKCE: Cognito only requires `code_verifier` if the original `/oauth2/authorize`
     * request used PKCE (`code_challenge`). Our primary flow uses oidc-client-ts
     * PKCE; this path is for non-PKCE direct visits. Do not add `code_verifier`
     * here unless it matches how the code was issued.
     */
    async function exchangeCodeDirectly(
      code: string
    ): Promise<{ access_token: string; id_token?: string }> {
      const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID as string;
      const redirectUri = process.env
        .VUE_APP_COGNITO_REDIRECT_SIGN_IN as string;
      const domain = process.env.VUE_APP_COGNITO_DOMAIN as string;
      const tokenEndpoint = `https://${domain}/oauth2/token`;

      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
      });

      const response = await fetch(tokenEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Token exchange failed: ${text}`);
      }

      const tokens = await response.json();
      if (!tokens.access_token) {
        throw new Error("No access_token in token response.");
      }
      return tokens as { access_token: string; id_token?: string };
    }

    onMounted(async () => {
      try {
        let accessToken: string | undefined;
        let email = "";
        let displayName = "";

        try {
          const user = await cognitoSigninCallbackFromRedirect();
          if (!user?.access_token) {
            throw new Error("No access token returned from signinCallback.");
          }
          accessToken = user.access_token;
          // email is in the OIDC profile (id_token claims)
          email = (user.profile?.email as string) ?? "";
          displayName =
            (user.profile?.name as string)?.trim() ||
            displayNameFromClaims(
              user.id_token
                ? decodeJwtPayload(user.id_token)
                : (user.profile as Record<string, unknown>) ?? {}
            );
        } catch (oidcErr: unknown) {
          const oidcMsg = (oidcErr as Error)?.message ?? "";
          // "No matching state" | "No state in response" — fall back to token endpoint + code
          const canFallback =
            oidcMsg.includes("No matching state") ||
            oidcMsg.includes("No state in response");
          if (canFallback) {
            console.warn(
              "[CognitoCallbackView] OIDC state error — falling back to direct code exchange"
            );
            const params = new URLSearchParams(window.location.search);
            const code = params.get("code");
            if (!code) throw new Error("No authorization code in URL.");
            const tokens = await exchangeCodeDirectly(code);
            accessToken = tokens.access_token;
            // Decode id_token to get email
            const idPayload = decodeJwtPayload(tokens.id_token ?? "");
            email = (idPayload.email as string) ?? "";
            displayName = displayNameFromClaims(idPayload);
          } else {
            throw oidcErr;
          }
        }

        if (!accessToken) {
          throw new Error("No access token after Cognito callback.");
        }

        await exchangeWithRails(accessToken, email, displayName);
        broadcastAppSwitchAfterCompanyCallbackSuccess();
      } catch (err: unknown) {
        clearCompanyCrossAppSwitchSiblingBroadcastPending();
        if (
          axios.isAxiosError(err) &&
          err.response?.status === 401 &&
          err.response.data !== undefined &&
          axiosResponseIsAccountDisabled(err.response.data)
        ) {
          clearCompanyAppSessionAndSetDisabledAlert();
          return;
        }
        if (
          axios.isAxiosError(err) &&
          err.response?.status === 422 &&
          isInviteInvalidError(err)
        ) {
          clearPendingInviteTokensIfInvalidInviteError(err);
          {
            const inviteCode = getCompanyInviteErrorCode(err);
            if (
              inviteCode === "invite_required" ||
              inviteCode === "invite_required_no_expert_account"
            ) {
              const display = companyInviteErrorDisplayForCode(inviteCode);
              errorHeading.value = display.heading;
              errorDetail.value = display.detail;
              startRedirectCountdown();
              return;
            }
          }
          goToExpertInviteErrorInterstitial(router, err);
          return;
        }
        clearPendingInviteTokensIfInvalidInviteError(err);
        console.error("[CognitoCallbackView] sign-in failed:", err);
        const display = isInviteInvalidError(err)
          ? inviteInvalidFriendlyError()
          : friendlySignInFailureDisplay(err);
        errorHeading.value = display.heading;
        errorDetail.value = display.detail;
        startRedirectCountdown();
      }
    });

    return {
      errorHeading,
      errorDetail,
      redirectSecondsRemaining,
      returnToHome,
    };
  },
});
</script>
